import axios from 'axios';
import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { FaEye, FaEyeSlash, FaUser, FaLock, FaPhone, FaUserPlus, FaEnvelope, FaCheckCircle, FaChevronDown } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { BASE_API_URL, RECAPTCHA_SITE_KEY } from '../helpers/Config';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const RegisterScreen = ({ theme = 'light' }) => {
    const isDarkMode = theme === 'dark';
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [recaptchaError, setRecaptchaError] = useState('');
    const recaptchaRef = useRef(null);
    const recaptchaWidgetId = useRef(null);

    const navigate = useNavigate();
    const { refer_code } = useParams();

    // Country codes with popular countries, sorted alphabetically
    const countryCodes = [
        { code: '1', country: 'United States' },
        { code: '7', country: 'Russia' },
        { code: '20', country: 'Egypt' },
        { code: '27', country: 'South Africa' },
        { code: '30', country: 'Greece' },
        { code: '31', country: 'Netherlands' },
        { code: '32', country: 'Belgium' },
        { code: '33', country: 'France' },
        { code: '34', country: 'Spain' },
        { code: '36', country: 'Hungary' },
        { code: '39', country: 'Italy' },
        { code: '40', country: 'Romania' },
        { code: '41', country: 'Switzerland' },
        { code: '43', country: 'Austria' },
        { code: '44', country: 'United Kingdom' },
        { code: '45', country: 'Denmark' },
        { code: '46', country: 'Sweden' },
        { code: '47', country: 'Norway' },
        { code: '48', country: 'Poland' },
        { code: '49', country: 'Germany' },
        { code: '51', country: 'Peru' },
        { code: '52', country: 'Mexico' },
        { code: '53', country: 'Cuba' },
        { code: '54', country: 'Argentina' },
        { code: '55', country: 'Brazil' },
        { code: '56', country: 'Chile' },
        { code: '57', country: 'Colombia' },
        { code: '58', country: 'Venezuela' },
        { code: '60', country: 'Malaysia' },
        { code: '61', country: 'Australia' },
        { code: '62', country: 'Indonesia' },
        { code: '63', country: 'Philippines' },
        { code: '64', country: 'New Zealand' },
        { code: '65', country: 'Singapore' },
        { code: '66', country: 'Thailand' },
        { code: '81', country: 'Japan' },
        { code: '82', country: 'South Korea' },
        { code: '84', country: 'Vietnam' },
        { code: '86', country: 'China' },
        { code: '90', country: 'Turkey' },
        { code: '91', country: 'India' },
        { code: '92', country: 'Pakistan' },
        { code: '93', country: 'Afghanistan' },
        { code: '94', country: 'Sri Lanka' },
        { code: '95', country: 'Myanmar' },
        { code: '98', country: 'Iran' },
        { code: '212', country: 'Morocco' },
        { code: '213', country: 'Algeria' },
        { code: '216', country: 'Tunisia' },
        { code: '218', country: 'Libya' },
        { code: '220', country: 'Gambia' },
        { code: '221', country: 'Senegal' },
        { code: '222', country: 'Mauritania' },
        { code: '223', country: 'Mali' },
        { code: '224', country: 'Guinea' },
        { code: '225', country: 'Ivory Coast' },
        { code: '226', country: 'Burkina Faso' },
        { code: '227', country: 'Niger' },
        { code: '228', country: 'Togo' },
        { code: '229', country: 'Benin' },
        { code: '230', country: 'Mauritius' },
        { code: '231', country: 'Liberia' },
        { code: '232', country: 'Sierra Leone' },
        { code: '233', country: 'Ghana' },
        { code: '234', country: 'Nigeria' },
        { code: '235', country: 'Chad' },
        { code: '236', country: 'Central African Republic' },
        { code: '237', country: 'Cameroon' },
        { code: '238', country: 'Cape Verde' },
        { code: '239', country: 'Sao Tome and Principe' },
        { code: '240', country: 'Equatorial Guinea' },
        { code: '241', country: 'Gabon' },
        { code: '242', country: 'Republic of the Congo' },
        { code: '243', country: 'Democratic Republic of the Congo' },
        { code: '244', country: 'Angola' },
        { code: '245', country: 'Guinea-Bissau' },
        { code: '246', country: 'British Indian Ocean Territory' },
        { code: '248', country: 'Seychelles' },
        { code: '249', country: 'Sudan' },
        { code: '250', country: 'Rwanda' },
        { code: '251', country: 'Ethiopia' },
        { code: '252', country: 'Somalia' },
        { code: '253', country: 'Djibouti' },
        { code: '254', country: 'Kenya' },
        { code: '255', country: 'Tanzania' },
        { code: '256', country: 'Uganda' },
        { code: '257', country: 'Burundi' },
        { code: '258', country: 'Mozambique' },
        { code: '260', country: 'Zambia' },
        { code: '261', country: 'Madagascar' },
        { code: '262', country: 'Reunion' },
        { code: '263', country: 'Zimbabwe' },
        { code: '264', country: 'Namibia' },
        { code: '265', country: 'Malawi' },
        { code: '266', country: 'Lesotho' },
        { code: '267', country: 'Botswana' },
        { code: '268', country: 'Swaziland' },
        { code: '269', country: 'Comoros' },
        { code: '290', country: 'Saint Helena' },
        { code: '291', country: 'Eritrea' },
        { code: '297', country: 'Aruba' },
        { code: '298', country: 'Faroe Islands' },
        { code: '299', country: 'Greenland' },
        { code: '350', country: 'Gibraltar' },
        { code: '351', country: 'Portugal' },
        { code: '352', country: 'Luxembourg' },
        { code: '353', country: 'Ireland' },
        { code: '354', country: 'Iceland' },
        { code: '355', country: 'Albania' },
        { code: '356', country: 'Malta' },
        { code: '357', country: 'Cyprus' },
        { code: '358', country: 'Finland' },
        { code: '359', country: 'Bulgaria' },
        { code: '370', country: 'Lithuania' },
        { code: '371', country: 'Latvia' },
        { code: '372', country: 'Estonia' },
        { code: '373', country: 'Moldova' },
        { code: '374', country: 'Armenia' },
        { code: '375', country: 'Belarus' },
        { code: '376', country: 'Andorra' },
        { code: '377', country: 'Monaco' },
        { code: '378', country: 'San Marino' },
        { code: '379', country: 'Vatican City' },
        { code: '380', country: 'Ukraine' },
        { code: '381', country: 'Serbia' },
        { code: '382', country: 'Montenegro' },
        { code: '383', country: 'Kosovo' },
        { code: '385', country: 'Croatia' },
        { code: '386', country: 'Slovenia' },
        { code: '387', country: 'Bosnia and Herzegovina' },
        { code: '389', country: 'North Macedonia' },
        { code: '420', country: 'Czech Republic' },
        { code: '421', country: 'Slovakia' },
        { code: '423', country: 'Liechtenstein' },
        { code: '500', country: 'Falkland Islands' },
        { code: '501', country: 'Belize' },
        { code: '502', country: 'Guatemala' },
        { code: '503', country: 'El Salvador' },
        { code: '504', country: 'Honduras' },
        { code: '505', country: 'Nicaragua' },
        { code: '506', country: 'Costa Rica' },
        { code: '507', country: 'Panama' },
        { code: '508', country: 'Saint Pierre and Miquelon' },
        { code: '509', country: 'Haiti' },
        { code: '590', country: 'Guadeloupe' },
        { code: '591', country: 'Bolivia' },
        { code: '592', country: 'Guyana' },
        { code: '593', country: 'Ecuador' },
        { code: '594', country: 'French Guiana' },
        { code: '595', country: 'Paraguay' },
        { code: '596', country: 'Martinique' },
        { code: '597', country: 'Suriname' },
        { code: '598', country: 'Uruguay' },
        { code: '599', country: 'Netherlands Antilles' },
        { code: '670', country: 'East Timor' },
        { code: '672', country: 'Norfolk Island' },
        { code: '673', country: 'Brunei' },
        { code: '674', country: 'Nauru' },
        { code: '675', country: 'Papua New Guinea' },
        { code: '676', country: 'Tonga' },
        { code: '677', country: 'Solomon Islands' },
        { code: '678', country: 'Vanuatu' },
        { code: '679', country: 'Fiji' },
        { code: '680', country: 'Palau' },
        { code: '681', country: 'Wallis and Futuna' },
        { code: '682', country: 'Cook Islands' },
        { code: '683', country: 'Niue' },
        { code: '685', country: 'Samoa' },
        { code: '686', country: 'Kiribati' },
        { code: '687', country: 'New Caledonia' },
        { code: '688', country: 'Tuvalu' },
        { code: '689', country: 'French Polynesia' },
        { code: '690', country: 'Tokelau' },
        { code: '691', country: 'Micronesia' },
        { code: '692', country: 'Marshall Islands' },
        { code: '850', country: 'North Korea' },
        { code: '852', country: 'Hong Kong' },
        { code: '853', country: 'Macau' },
        { code: '855', country: 'Cambodia' },
        { code: '856', country: 'Laos' },
        { code: '872', country: 'International' },
        { code: '880', country: 'Bangladesh' },
        { code: '886', country: 'Taiwan' },
        { code: '960', country: 'Maldives' },
        { code: '961', country: 'Lebanon' },
        { code: '962', country: 'Jordan' },
        { code: '963', country: 'Syria' },
        { code: '964', country: 'Iraq' },
        { code: '965', country: 'Kuwait' },
        { code: '966', country: 'Saudi Arabia' },
        { code: '967', country: 'Yemen' },
        { code: '968', country: 'Oman' },
        { code: '970', country: 'Palestine' },
        { code: '971', country: 'United Arab Emirates' },
        { code: '972', country: 'Israel' },
        { code: '973', country: 'Bahrain' },
        { code: '974', country: 'Qatar' },
        { code: '975', country: 'Bhutan' },
        { code: '976', country: 'Mongolia' },
        { code: '977', country: 'Nepal' },
        { code: '992', country: 'Tajikistan' },
        { code: '993', country: 'Turkmenistan' },
        { code: '994', country: 'Azerbaijan' },
        { code: '995', country: 'Georgia' },
        { code: '996', country: 'Kyrgyzstan' },
        { code: '998', country: 'Uzbekistan' }
    ].sort((a, b) => a.country.localeCompare(b.country)); // Sort alphabetically by country name

    // Validation Schema
    const validationSchema = Yup.object({
        name: Yup.string()
            .required('Full name is required')
            .min(2, 'Name must be at least 2 characters')
            .max(50, 'Name must be less than 50 characters'),
        email: Yup.string()
            .required('Email address is required')
            .email('Please enter a valid email address'),
        mobile: Yup.string()
            .required('Mobile number is required')
            .matches(/^\d+$/, 'Mobile number must contain only digits')
            .min(1, 'Mobile number is required'),
        countryCode: Yup.string()
            .required('Country code is required'),
        password: Yup.string()
            .required('Password is required')
            .min(6, 'Password must be at least 6 characters'),
        confirmPassword: Yup.string()
            .required('Please confirm your password')
            .oneOf([Yup.ref('password')], 'Passwords must match'),
        referralCode: Yup.string()
            .required('Referral code is required')
            .min(3, 'Referral code must be at least 3 characters')
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            email: '',
            mobile: '',
            countryCode: '91', // India as default
            password: '',
            confirmPassword: '',
            referralCode: refer_code || ''
        },
        validationSchema,
        onSubmit: async (values) => {
            setIsLoading(true);
            try {
                setRecaptchaError('');
                let recaptchaToken = null;

                if (RECAPTCHA_SITE_KEY && typeof window !== 'undefined' && window.grecaptcha) {
                    recaptchaToken = window.grecaptcha.getResponse();
                    if (!recaptchaToken) {
                        setRecaptchaError('Please complete the reCAPTCHA verification.');
                        setIsLoading(false);
                        return;
                    }
                }

                const payload = {
                    name: values.name,
                    email: values.email,
                    mobile: values.mobile,
                    country_code: values.countryCode,
                    password: values.password,
                    refer_code: values.referralCode
                };

                if (recaptchaToken) {
                    payload.recaptcha_token = recaptchaToken;
                }

                const res = await axios.post(`${BASE_API_URL}/auth/register`, payload);
                const data = res?.data;

                if (data?.error) {
                    toast.error(data?.error);
                    return;
                }

                if (data?.msg) {
                    toast.success(data?.msg);
                    const token = data?.token;
                    const balance = data?.balance;
                    const usdt = data?.usdt;
                    localStorage.setItem("a5srf4wer7w1cd", token);
                    localStorage.setItem("balance", balance);
                    localStorage.setItem("usdt_balance", usdt);
                    navigate('/', { replace: true });
                }

            } catch (error) {
                console.error('Registration error:', error);
                if (error.response?.data?.error) {
                    toast.error(error.response.data.error);
                } else {
                    toast.error('Registration failed. Please try again.');
                }
            } finally {
                setIsLoading(false);
                if (typeof window !== 'undefined' && window.grecaptcha && recaptchaWidgetId.current !== null) {
                    window.grecaptcha.reset(recaptchaWidgetId.current);
                }
            }
        }
    });

    // Move selectedCountry after formik initialization
    const selectedCountry = countryCodes.find(country => country.code === formik.values.countryCode);

    useEffect(() => {
        if (refer_code) {
            formik.setFieldValue('referralCode', refer_code);
        }
    }, [refer_code]);

    // Load Google reCAPTCHA v2 script and render visible checkbox
    useEffect(() => {
        if (!RECAPTCHA_SITE_KEY || typeof window === 'undefined') return;

        const scriptId = 'recaptcha-v2-script';
        const oldV3Script = document.getElementById('recaptcha-v3-script');
        if (oldV3Script) oldV3Script.remove();

        const renderRecaptcha = () => {
            if (recaptchaRef.current && window.grecaptcha && !recaptchaWidgetId.current) {
                try {
                    recaptchaWidgetId.current = window.grecaptcha.render(recaptchaRef.current, {
                        sitekey: RECAPTCHA_SITE_KEY,
                        theme: theme === 'dark' ? 'dark' : 'light'
                    });
                } catch (e) {
                    console.error('reCAPTCHA render error:', e);
                }
            }
        };

        if (document.getElementById(scriptId)) {
            renderRecaptcha();
            return;
        }

        const script = document.createElement('script');
        script.id = scriptId;
        script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad`;
        script.async = true;
        script.defer = true;

        window.onRecaptchaLoad = () => {
            renderRecaptcha();
            delete window.onRecaptchaLoad;
        };

        document.body.appendChild(script);

        return () => {
            recaptchaWidgetId.current = null;
        };
    }, [RECAPTCHA_SITE_KEY, theme]);

    const handleCountrySelect = (countryCode) => {
        formik.setFieldValue('countryCode', countryCode);
        setShowCountryDropdown(false);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    // Theme-aware styles
    const styles = {
        container: {
            width: '100%',
            maxWidth: '100%',
            background: 'transparent',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            padding: '10px'
        },
        formContainer: {
            background: isDarkMode ? '#2d3748' : '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: isDarkMode
                ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                : '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: isDarkMode ? '1px solid #4a5568' : '1px solid #e2e8f0',
            maxWidth: '420px',
            margin: '0 auto'
        },
        header: {
            textAlign: 'center',
            marginBottom: '20px'
        },
        logoIcon: {
            fontSize: '32px',
            color: isDarkMode ? '#68d391' : '#38a169',
            background: isDarkMode
                ? 'linear-gradient(135deg, #2d3748, #4a5568)'
                : 'linear-gradient(135deg, #f7fafc, #edf2f7)',
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px',
            boxShadow: isDarkMode
                ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                : '0 2px 8px rgba(0, 0, 0, 0.1)'
        },
        title: {
            fontSize: '20px',
            fontWeight: '700',
            color: isDarkMode ? '#e2e8f0' : '#2d3748',
            margin: '0 0 6px 0'
        },
        subtitle: {
            fontSize: '12px',
            color: isDarkMode ? '#a0aec0' : '#718096',
            margin: 0,
            fontWeight: '400'
        },
        form: {
            width: '100%'
        },
        formGroup: {
            marginBottom: '16px'
        },
        labelContainer: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '6px'
        },
        labelIcon: {
            color: isDarkMode ? '#68d391' : '#38a169',
            fontSize: '12px',
            marginRight: '6px'
        },
        label: {
            fontSize: '12px',
            fontWeight: '600',
            color: isDarkMode ? '#e2e8f0' : '#2d3748'
        },
        inputContainer: {
            position: 'relative',
            width: '100%'
        },
        input: {
            width: '100%',
            padding: '10px 12px',
            fontSize: '14px',
            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
            borderRadius: '6px',
            backgroundColor: isDarkMode ? '#4a5568' : '#ffffff',
            color: isDarkMode ? '#e2e8f0' : '#2d3748',
            transition: 'all 0.2s ease',
            outline: 'none',
            boxSizing: 'border-box',
            fontWeight: '400'
        },
        inputError: {
            borderColor: '#fc8181',
            backgroundColor: isDarkMode ? '#742a2a' : '#fff5f5'
        },
        inputSuccess: {
            borderColor: '#68d391'
        },
        successIcon: {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#68d391',
            fontSize: '14px'
        },
        errorText: {
            color: '#fc8181',
            fontSize: '11px',
            marginTop: '4px',
            display: 'block',
            fontWeight: '500'
        },
        passwordToggle: {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            cursor: 'pointer',
            color: isDarkMode ? '#a0aec0' : '#718096',
            fontSize: '14px',
            zIndex: 2,
            transition: 'color 0.2s ease'
        },
        toggleIcon: {
            fontSize: '14px'
        },
        mobileInputContainer: {
            display: 'flex',
            gap: '8px',
            alignItems: 'center'
        },
        countrySelector: {
            position: 'relative',
            minWidth: '120px'
        },
        countryButton: {
            padding: '10px 8px',
            fontSize: '14px',
            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
            borderRadius: '6px',
            backgroundColor: isDarkMode ? '#4a5568' : '#ffffff',
            color: isDarkMode ? '#e2e8f0' : '#2d3748',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '4px',
            minWidth: '120px',
            height: '40px'
        },
        countryDropdown: {
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: isDarkMode ? '#4a5568' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#718096' : '#e2e8f0'}`,
            borderRadius: '6px',
            boxShadow: isDarkMode
                ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                : '0 2px 8px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto'
        },
        countryOption: {
            padding: '10px 12px',
            fontSize: '14px',
            color: isDarkMode ? '#e2e8f0' : '#2d3748',
            cursor: 'pointer',
            borderBottom: `1px solid ${isDarkMode ? '#718096' : '#e2e8f0'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        countryOptionHover: {
            backgroundColor: isDarkMode ? '#718096' : '#edf2f7'
        },
        mobileInput: {
            flex: 1
        },
        recaptchaContainer: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: '16px',
            marginBottom: '4px',
            minHeight: '78px'
        },
        buttonContainer: {
            marginTop: '20px'
        },
        registerButton: {
            width: '100%',
            padding: '12px',
            fontSize: '14px',
            fontWeight: '600',
            color: '#ffffff',
            background: isDarkMode
                ? 'linear-gradient(135deg, #68d391, #38a169)'
                : 'linear-gradient(135deg, #38a169, #2f855a)',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: isDarkMode
                ? '0 2px 8px rgba(104, 211, 145, 0.3)'
                : '0 2px 8px rgba(56, 161, 105, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
        },
        registerButtonDisabled: {
            opacity: 0.6,
            cursor: 'not-allowed'
        },
        loadingContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
        },
        spinner: {
            width: '16px',
            height: '16px',
            border: '2px solid transparent',
            borderTop: '2px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
        },
        loginSection: {
            textAlign: 'center',
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
        },
        loginText: {
            color: isDarkMode ? '#a0aec0' : '#718096',
            fontSize: '12px',
            margin: 0
        },
        loginLink: {
            color: isDarkMode ? '#68d391' : '#38a169',
            textDecoration: 'none',
            fontWeight: '600',
            transition: 'all 0.2s ease'
        }
    };

    return (
        <>
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="register-container"
                style={styles.container}
            >
                <div className="register-content">
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.1, duration: 0.5 }}
                        className="register-form-container"
                        style={styles.formContainer}
                    >
                        {/* Header Section */}
                        <div className="register-header" style={styles.header}>
                            <div className="logo">
                                <div className="logo-icon" style={styles.logoIcon}>
                                    <FaUserPlus />
                                </div>
                            </div>
                            <h1 style={styles.title}>Create Account</h1>
                            <p style={styles.subtitle}>Join our platform today</p>
                        </div>

                        {/* Register Form */}
                        <form onSubmit={formik.handleSubmit} style={styles.form}>
                            {/* Name Field */}
                            <div className="form-group" style={styles.formGroup}>
                                <div style={styles.labelContainer}>
                                    <FaUser style={styles.labelIcon} />
                                    <label style={styles.label}>Full Name *</label>
                                </div>
                                <div className="input-container" style={styles.inputContainer}>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="Enter your full name"
                                        required
                                        className="form-input"
                                        style={{
                                            ...styles.input,
                                            ...(formik.touched.name && formik.errors.name && styles.inputError),
                                            ...(formik.values.name && !formik.errors.name && styles.inputSuccess)
                                        }}
                                        disabled={isLoading}
                                    />
                                    {formik.values.name && !formik.errors.name && (
                                        <FaCheckCircle style={styles.successIcon} />
                                    )}
                                </div>
                                {formik.touched.name && formik.errors.name && (
                                    <span style={styles.errorText}>{formik.errors.name}</span>
                                )}
                            </div>

                            {/* Email Field */}
                            <div className="form-group" style={styles.formGroup}>
                                <div style={styles.labelContainer}>
                                    <FaEnvelope style={styles.labelIcon} />
                                    <label style={styles.label}>Email Address *</label>
                                </div>
                                <div className="input-container" style={styles.inputContainer}>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="Enter your email address"
                                        required
                                        className="form-input"
                                        style={{
                                            ...styles.input,
                                            ...(formik.touched.email && formik.errors.email && styles.inputError),
                                            ...(formik.values.email && !formik.errors.email && styles.inputSuccess)
                                        }}
                                        disabled={isLoading}
                                    />
                                    {formik.values.email && !formik.errors.email && (
                                        <FaCheckCircle style={styles.successIcon} />
                                    )}
                                </div>
                                {formik.touched.email && formik.errors.email && (
                                    <span style={styles.errorText}>{formik.errors.email}</span>
                                )}
                            </div>

                            {/* Mobile Field with Country Code */}
                            <div className="form-group" style={styles.formGroup}>
                                <div style={styles.labelContainer}>
                                    <FaPhone style={styles.labelIcon} />
                                    <label style={styles.label}>Mobile Number *</label>
                                </div>
                                <div className="mobile-input-container" style={styles.mobileInputContainer}>
                                    {/* Country Code Dropdown */}
                                    <div className="country-selector" style={styles.countrySelector}>
                                        <button
                                            type="button"
                                            className="country-button"
                                            style={styles.countryButton}
                                            onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                            disabled={isLoading}
                                        >
                                            <span>+{selectedCountry?.code}</span>
                                            <FaChevronDown size={10} />
                                        </button>
                                        {showCountryDropdown && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="country-dropdown"
                                                style={styles.countryDropdown}
                                            >
                                                {countryCodes.map((country) => (
                                                    <div
                                                        key={country.code}
                                                        className="country-option"
                                                        style={{
                                                            ...styles.countryOption,
                                                            ...(formik.values.countryCode === country.code && styles.countryOptionHover)
                                                        }}
                                                        onClick={() => handleCountrySelect(country.code)}
                                                    >
                                                        <span>{country.country}</span>
                                                        <span style={{ fontSize: '12px', color: isDarkMode ? '#a0aec0' : '#718096' }}>
                                                            +{country.code}
                                                        </span>
                                                    </div>
                                                ))}
                                            </motion.div>
                                        )}
                                    </div>
                                    {/* Mobile Number Input */}
                                    <div className="input-container" style={{ ...styles.inputContainer, ...styles.mobileInput }}>
                                        <input
                                            type="tel"
                                            name="mobile"
                                            value={formik.values.mobile}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Enter mobile number"
                                            required
                                            className="form-input"
                                            style={{
                                                ...styles.input,
                                                ...(formik.touched.mobile && formik.errors.mobile && styles.inputError),
                                                ...(formik.values.mobile && !formik.errors.mobile && styles.inputSuccess)
                                            }}
                                            disabled={isLoading}
                                        />
                                        {formik.values.mobile && !formik.errors.mobile && (
                                            <FaCheckCircle style={styles.successIcon} />
                                        )}
                                    </div>
                                </div>
                                {formik.touched.mobile && formik.errors.mobile && (
                                    <span style={styles.errorText}>{formik.errors.mobile}</span>
                                )}
                            </div>

                            {/* Password Field */}
                            <div className="form-group" style={styles.formGroup}>
                                <div style={styles.labelContainer}>
                                    <FaLock style={styles.labelIcon} />
                                    <label style={styles.label}>Password *</label>
                                </div>
                                <div className="input-container" style={styles.inputContainer}>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={formik.values.password}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="Create a password"
                                        required
                                        className="form-input"
                                        style={{
                                            ...styles.input,
                                            ...(formik.touched.password && formik.errors.password && styles.inputError),
                                            ...(formik.values.password && !formik.errors.password && styles.inputSuccess)
                                        }}
                                        disabled={isLoading}
                                    />
                                    <div
                                        className="password-toggle"
                                        style={styles.passwordToggle}
                                        onClick={togglePasswordVisibility}
                                    >
                                        {showPassword ? (
                                            <FaEyeSlash style={styles.toggleIcon} />
                                        ) : (
                                            <FaEye style={styles.toggleIcon} />
                                        )}
                                    </div>
                                </div>
                                {formik.touched.password && formik.errors.password && (
                                    <span style={styles.errorText}>{formik.errors.password}</span>
                                )}
                            </div>

                            {/* Confirm Password Field */}
                            <div className="form-group" style={styles.formGroup}>
                                <div style={styles.labelContainer}>
                                    <FaLock style={styles.labelIcon} />
                                    <label style={styles.label}>Confirm Password *</label>
                                </div>
                                <div className="input-container" style={styles.inputContainer}>
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        value={formik.values.confirmPassword}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="Confirm your password"
                                        required
                                        className="form-input"
                                        style={{
                                            ...styles.input,
                                            ...(formik.touched.confirmPassword && formik.errors.confirmPassword && styles.inputError),
                                            ...(formik.values.confirmPassword && !formik.errors.confirmPassword && styles.inputSuccess)
                                        }}
                                        disabled={isLoading}
                                    />
                                    <div
                                        className="password-toggle"
                                        style={styles.passwordToggle}
                                        onClick={toggleConfirmPasswordVisibility}
                                    >
                                        {showConfirmPassword ? (
                                            <FaEyeSlash style={styles.toggleIcon} />
                                        ) : (
                                            <FaEye style={styles.toggleIcon} />
                                        )}
                                    </div>
                                </div>
                                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                                    <span style={styles.errorText}>{formik.errors.confirmPassword}</span>
                                )}
                            </div>

                            {/* Referral Code Field */}
                            <div className="form-group" style={styles.formGroup}>
                                <div style={styles.labelContainer}>
                                    <FaUserPlus style={styles.labelIcon} />
                                    <label style={styles.label}>Referral Code *</label>
                                </div>
                                <div className="input-container" style={styles.inputContainer}>
                                    <input
                                        type="text"
                                        name="referralCode"
                                        value={formik.values.referralCode}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="Enter referral code"
                                        required
                                        className="form-input"
                                        style={{
                                            ...styles.input,
                                            ...(formik.touched.referralCode && formik.errors.referralCode && styles.inputError),
                                            ...(formik.values.referralCode && !formik.errors.referralCode && styles.inputSuccess)
                                        }}
                                        disabled={isLoading}
                                    />
                                    {formik.values.referralCode && !formik.errors.referralCode && (
                                        <FaCheckCircle style={styles.successIcon} />
                                    )}
                                </div>
                                {formik.touched.referralCode && formik.errors.referralCode && (
                                    <span style={styles.errorText}>{formik.errors.referralCode}</span>
                                )}
                            </div>

                            {/* reCAPTCHA v2 */}
                            {RECAPTCHA_SITE_KEY && (
                                <div className="form-group" style={styles.formGroup}>
                                    <div style={styles.recaptchaContainer} ref={recaptchaRef} />
                                    {recaptchaError && (
                                        <span style={styles.errorText}>{recaptchaError}</span>
                                    )}
                                </div>
                            )}

                            {/* Register Button */}
                            <div style={styles.buttonContainer}>
                                <button
                                    type="submit"
                                    className="register-button"
                                    style={{
                                        ...styles.registerButton,
                                        ...(isLoading && styles.registerButtonDisabled)
                                    }}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <div style={styles.loadingContainer}>
                                            <div style={styles.spinner}></div>
                                            Creating Account...
                                        </div>
                                    ) : (
                                        'Create Account'
                                    )}
                                </button>
                            </div>
                        </form>

                        {/* Login Link */}
                        <div className="login-section" style={styles.loginSection}>
                            <p style={styles.loginText}>
                                Already have an account?{' '}
                                <a
                                    href="/login"
                                    style={styles.loginLink}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate('/login');
                                    }}
                                >
                                    Sign In
                                </a>
                            </p>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
        </>
    );
};

export default RegisterScreen;